import { Button } from '@/components/ui/button';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router-dom';
import { useUpdateMaintenanceDataMutation } from '@/store/api/maintenanceApi';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';
import { DropdownInput } from '.';
import { renderDynamicInputList } from './form/components/DynamicInputs';
import { MaintenanceSchema } from './form/schema/maintenanceSchema';
import { prepareDefaultValues } from './form/utils';
import { dynamicInputListConfig } from './form/dynamicInputListConfig';
import { toast, useAppFilters, useToast } from '@/hooks';
import { useTranslation } from 'react-i18next';

interface MaintenanceDataFormProps {
  row: any;
  DMComponents?: any[];
  closeDrawer: any;
}

export function MaintenanceDataForm({ row, DMComponents, closeDrawer }: MaintenanceDataFormProps) {
  const { t } = useTranslation();
  const { selectedComponent, selectedAsset } = useAppFilters();
  const { isMobileAsset } = useActiveAssetsType();
  const { id, siteId } = useParams();
  const [updateMaintenanceData, { isSuccess, isError, error }] = useUpdateMaintenanceDataMutation();
  // Initialize form with React Hook Form & Zod
  const {
    handleSubmit,
    formState: { errors },
    getValues,
    control,
  } = useForm({
    resolver: zodResolver(MaintenanceSchema),
    defaultValues: prepareDefaultValues(row),
    mode: 'onChange', // This triggers validation on field change
  });

  const values = getValues();
  const { toast } = useToast();

  // @TODO: refactor - e.g. componentId should not be based on names
  // Handle form submission

  const onSubmit = async () => {
    if (!isMobileAsset) {
      return;
    }
    closeDrawer();

    const data: typeof values = getValues();
    if (!data) return;
    const transformedData = Object.keys(data).reduce((acc, key) => {
      acc[key] = data[key as 'comments'] === '-' ? null : data[key as 'comments'];
      return acc;
    }, {} as any);
    const selectedComponentId =
      transformedData.component &&
      DMComponents?.find((el) => el.component_ui_name === transformedData.component)?.component_id;
    transformedData['componentId'] = selectedComponentId;
    transformedData['assetId'] = id;
    const { workOrderId, componentId, comments } = transformedData;
    const params = {
      site_id: siteId,
      workOrderId,
      razorComments: comments.trim(),
      componentId: componentId || null,
      assetCategory: isMobileAsset ? 'mobile' : 'fixed',
      assetId: id,
    };
    const response: any = await updateMaintenanceData(params);
    if (response?.data?.status === 'ok') {
      toast({
        variant: 'success',
        title: t('maintenanceForm.success.title'),
        description: t('maintenanceForm.success.description'),
      });
    } else {
      toast({
        variant: 'destructive',
        title: t('maintenanceForm.error.title'),
        description: (error as any)?.message || t('maintenanceForm.error.description'),
      });
    }
    closeDrawer();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col px-4 py-6  overflow-x-hidden flex-1 justify-between mb-4"
    >
      <div className="flex flex-col gap-4 overflow-x-hidden">
        {/* Controller with Dropdown Menu */}
        {DMComponents && DMComponents.length > 0 && (
          <DropdownInput control={control} items={DMComponents} defaultValue={values.component} />
        )}
        {/* Dynamic inputs rendering*/}
        {renderDynamicInputList(
          dynamicInputListConfig.map((el: any) => {
            return { ...el, control, errors };
          })
        )}
      </div>
      <div className="flex items-center justify-end gap-4 px-4">
        <Button type="reset" className="mt-4 static" variant={'ghost'} onClick={closeDrawer} data-testid="edit_cancel">
          {t('maintenanceForm.cancel')}
        </Button>
        <Button type="submit" className="mt-4 static" variant={'filled'} data-testid="edit_save">
          {t('maintenanceForm.save')}
        </Button>
      </div>
    </form>
  );
}
